/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import axios from 'axios';

import styles from './Feedback.module.scss';

/**
 * This is the Feedback component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Feedback({
  labels,
  url,
  endpoint,
  hash,
  handles,
  csrf,
  payload,
  captchaLocale,
  captchaSiteKey,
}) {
  const [expanded, setExpanded] = useState(false);
  const [found, setFound] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [finished, setFinished] = useState(false);
  const [errors, setErrors] = useState(null);
  const [formErrors, setFormErrors] = useState(null);
  const formRef = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const payload = new FormData(e.target);

    setSubmitting(true);
    setSubmitted(false);

    // Clear previous error messages etc.
    setSuccess(null);
    setFinished(null);
    setFormErrors(null);
    setErrors(null);

    // Send POST request
    axios({
      url: endpoint ? endpoint : window.location.href,
      method: 'post',
      data: payload,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        HTTP_X_REQUESTED_WITH: 'XMLHttpRequest',
      },
    })
      .then((response) => {
        const data = response.data;
        setSubmitting(false);
        setSubmitted(true);

        setSuccess(data.success);
        setFinished(data.finished);
        setFormErrors(data.formErrors);
        setErrors(data.errors);

        if (data.success) {
          setMessage('');
        }
      })
      .catch((error) => {
        console.warn(error);
      });
  };

  const initForm = (ref) => {
    if (!formRef.current) {
      formRef.current = ref;
      formRef.current.addEventListener('freeform-on-submit', handleSubmit);
    }
  };

  return (
    <div className={classNames(styles.main)}>
      <div>
        <h3>{labels.heading}</h3>
        <form ref={initForm} method="post" data-freeform="">
          <input type="hidden" name="url" value={url} />
          <input type="hidden" name="action" value={endpoint} />
          <input type="hidden" name="freeform-action" value="submit" />
          <input type="hidden" name="freeform_form_handle" value="" />
          <input type="hidden" name="formHash" value={hash} />
          {csrf && <input type="hidden" name={csrf.name} value={csrf.token} />}
          {/* Honeypot field from freeform */}
          <div className={styles.honeypot} aria-hidden="true" tabIndex="-1">
            <label aria-hidden="true" tabIndex="-1" htmlFor="middlename">
              Leave this field blank
            </label>
            <input
              type="text"
              value=""
              name="middlename"
              id="middlename"
              aria-hidden="true"
              autoComplete="off"
              tabIndex="-1"
              readOnly
            />
          </div>

          <input type="hidden" name="freeform_payload" defaultValue={payload} />

          <div className={classNames(styles.buttons, 'form__row')}>
            <div className="form__column form__column-radio_group form__column-group form__column-group-one-line">
              <div>
                <label className={classNames(styles.radio)}>
                  <input
                    type="radio"
                    name={handles.radio}
                    value="yes"
                    className="form__input"
                    disabled={success}
                    checked={found === true}
                    onSelect={() => {
                      setExpanded(true);
                      setFound(true);
                    }}
                    onChange={() => {
                      setExpanded(true);
                      setFound(true);
                    }}
                  />{' '}
                  <span>{labels.buttons.yes}</span>
                </label>
                <label className={classNames(styles.radio)}>
                  <input
                    type="radio"
                    name={handles.radio}
                    value={found ? 'yes' : 'no'}
                    className="form__input"
                    disabled={success}
                    checked={found === false}
                    onSelect={() => {
                      setExpanded(true);
                      setFound(true);
                    }}
                    onChange={() => {
                      setExpanded(true);
                      setFound(false);
                    }}
                  />{' '}
                  <span>{labels.buttons.no}</span>
                </label>
              </div>
              {errors && errors.found && (
                <ul className="errors form__errors">
                  {errors.found.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div style={{ display: expanded ? 'block' : 'none' }}>
            <p className="form__required-fields">{labels.requiredFields}</p>
            <div className="form__row">
              <div className="form__column form__column-textarea">
                <label
                  htmlFor="feedback-message"
                  className="form__label form__label-required"
                >
                  {labels.message.labels[found ? 'yes' : 'no']}
                </label>
                <textarea
                  className="form__input"
                  id="feedback-message"
                  name={handles.textarea}
                  rows="4"
                  placeholder={labels.message.placeholder}
                  onChange={(e) => setMessage(e.target.value)}
                  value={message}
                  required
                />
                <div className="form__instructions">
                  {labels.message.instructions}
                </div>
                {errors && errors.message && (
                  <ul className="errors form__errors">
                    {errors.message.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div
              data-freeform-recaptcha-container
              data-captcha="recaptcha"
              data-sitekey={captchaSiteKey}
              data-theme="light"
              data-size="normal"
              data-version="v2-checkbox"
              data-action="submit"
              data-locale={captchaLocale}
            ></div>
            {!success && (
              <div className={classNames(styles.submitRow, 'form__row')}>
                <div className="form__column form__column-submit form__column-input-only form__column-content-align-left">
                  <button
                    className="form__submit"
                    type="submit"
                    name="form_page_submit"
                    disabled={!message || submitting}
                  >
                    {labels.buttons.submit}
                  </button>
                </div>
              </div>
            )}
            {submitted && !success && (
              <div>
                <p className={styles.error}>{labels.error}</p>
                {formErrors && (
                  <ul className="errors form__errors">
                    {formErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {submitted && success && <p>{labels.success}</p>}
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Feedback.propTypes = {
  url: PropTypes.string,
  endpoint: PropTypes.string,
  hash: PropTypes.string,
  csrf: PropTypes.shape({
    token: PropTypes.string,
    name: PropTypes.string,
  }),
  payload: PropTypes.string,
  handles: PropTypes.shape({
    radio: PropTypes.string,
    textarea: PropTypes.string,
  }),
  captchaLocale: PropTypes.oneOf(['nb', 'en']),
  captchaSiteKey: PropTypes.string,
  labels: PropTypes.shape({
    heading: PropTypes.string,
    success: PropTypes.string,
    error: PropTypes.string,
    requiredFields: PropTypes.string,
    buttons: PropTypes.shape({
      yes: PropTypes.string,
      no: PropTypes.string,
      submit: PropTypes.string,
    }),
    message: PropTypes.shape({
      labels: PropTypes.shape({
        yes: PropTypes.string,
        no: PropTypes.string,
      }),
      placeholder: PropTypes.string,
      instructions: PropTypes.string,
    }),
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Feedback.defaultProps = {
  url: '',
  endpoint: null,
  hash: null,
  csrf: {
    token: null,
    name: null,
  },
  payload: null,
  handles: {
    radio: 'found',
    textarea: 'message',
  },
  captchaLocale: 'nb',
  captchaSiteKey: null,
  labels: {
    heading: 'Fant du det du lette etter?',
    success: 'Takk, din tilbakemelding er mottatt!',
    error: 'Beklager, det oppstod en feil under mottak av din tilbakemelding',
    requiredFields: 'Obligatoriske felter er merket med *',
    message: {
      labels: {
        yes: 'Dine innspill hjelper oss med å forbedre nettstedet. Ikke legg igjen personlige opplysninger. Vi svarer heller ikke på henvendelser.',
        no: 'Dine innspill hjelper oss med å forbedre nettstedet. Ikke legg igjen personlige opplysninger. Vi svarer heller ikke på henvendelser.,',
      },
      placeholder: 'Tilbakemelding',
      instructions:
        'Ikke legg igjen personlige opplysninger. Vi svarer heller ikke på henvendelser.',
    },
    buttons: {
      yes: 'Ja',
      no: 'Nei',
      submit: 'Send inn',
    },
  },
};

export default Feedback;
